import React, { useEffect, useState } from 'react';
import { CardHeader, Collapse } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { NSButton, NSCard } from 'bricks';
import { useCreateProjectAsset, useGetProjectAssets } from 'views/DocumentLibrary/hooks';
import useAwsUploadFiles from 'hooks/useAWSUpload';
import useAuthContext from 'contexts/AuthContext';
import { FEATURES_LIST } from 'services/constants';
import useToast from 'hooks/useToast';
import { normalizeFileFormat } from 'views/DocumentLibrary/helpers';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS, URLS } from 'services/urls';
import DropzoneCard from '../DropzoneCard/DropzoneCard';
import ProjectAssetTable from './ProjectAssetTable';

export interface IProjectCardProps {
    subprojectId: number;
    subprojectCorrelationId: string;
    projectName: string;
    developmentName: string;
}

export const ProjectCard = ({
    subprojectId,
    subprojectCorrelationId,
    projectName,
    developmentName,
}: IProjectCardProps) => {
    const { selectedOrganizationId } = useAuthContext();
    const { showError, showSuccess } = useToast();
    const queryClient = useQueryClient();
    const {
        selectedHTML5FileToUpload,
        setSelectedHTML5FileToUpload,
        clearAttachmentsStates,
        awsUploadedFiles,
        setAwsUploadedFiles,
        handleUpload,
    } = useAwsUploadFiles({
        organizationCorrelationId: selectedOrganizationId!,
        feature: FEATURES_LIST.DEAL_DOCUMENTS,
        onError: (message: string) => {
            showError(`Failed to upload file: ${message}`);
        },
    });

    const { mutate: createProjectAsset } = useCreateProjectAsset();

    const getFilesFromEvent = async (
        event: React.DragEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>,
        dropLocation: string,
    ): Promise<File[]> => {
        const allowFolderUpload = false;
        const files: File[] = [];
        let hasShownFolderWarning = false;

        if ('dataTransfer' in event && event.dataTransfer?.items) {
            const items = Array.from(event.dataTransfer.items)
                .filter(item => {
                    const entry = item.webkitGetAsEntry?.();
                    if (entry && entry.isDirectory && !allowFolderUpload) {
                        if (!hasShownFolderWarning) {
                            showError('Folder uploads are not allowed. Please upload individual files.');
                            hasShownFolderWarning = true;
                        }
                        return false;
                    }
                    return true;
                })
                .map(item => item.getAsFile?.())
                .filter((file): file is File => !!file);

            files.push(...items);
        } else if ('target' in event && (event.target as HTMLInputElement)?.files) {
            const fileList = Array.from((event.target as HTMLInputElement).files!);
            const filteredFiles = fileList.filter(file => {
                if (!allowFolderUpload && file.webkitRelativePath && file.webkitRelativePath !== '') {
                    if (!hasShownFolderWarning) {
                        showError('Folder uploads are not allowed. Please upload individual files.');
                        hasShownFolderWarning = true;
                    }
                    return false;
                }
                return true;
            });

            files.push(...filteredFiles);
        }

        const newIndexedFiles = files.map(file => ({
            file,
            id: crypto.randomUUID(),
        }));

        setSelectedHTML5FileToUpload(prev => [...prev, ...newIndexedFiles]);
        return files;
    };

    useEffect(() => {
        if (selectedHTML5FileToUpload.length > 0) {
            handleUpload();
        }
    }, [selectedHTML5FileToUpload]);

    useEffect(() => {
        if (awsUploadedFiles.length && awsUploadedFiles.length === selectedHTML5FileToUpload.length) {
            const postData: { name: string; extension: string; size: string; s3Url: string }[] = [];
            awsUploadedFiles.forEach(uploadedFile => {
                const postFile = selectedHTML5FileToUpload.find(file => file.id === uploadedFile.id);
                if (!postFile) return;

                postData.push({
                    name: postFile.file.name,
                    extension: normalizeFileFormat(postFile.file.type)!,
                    size: String(Math.round(postFile.file.size / 100)),
                    s3Url: uploadedFile.url!,
                });
            });

            createProjectAsset({
                projectUuid: subprojectCorrelationId,
                postData,
            },
            {
                onSuccess: data => {
                    showSuccess('Document uploaded successfully');
                    setAwsUploadedFiles([]);
                    clearAttachmentsStates();
                    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PROJECT_ASSETS, subprojectCorrelationId] });
                },
                onError: error => {
                    setAwsUploadedFiles([]);
                    clearAttachmentsStates();
                    showError('Failed to upload document. Please try again.');
                },
            });
        }
    }, [awsUploadedFiles, selectedHTML5FileToUpload]);

    const [isExpandedState, setExpandedState] = useState(false);

    const { data: projectAssets } = useGetProjectAssets(subprojectCorrelationId);

    const toggleExpanded = () => {
        setExpandedState(!isExpandedState);
    };

    const RedirectToCoreDocumentPage = () => {
        window.open(URLS.PROJECTS_APP.DOCUMENT_LIBRARY(subprojectId), '_blank');
    };

    return (
        <NSCard className="NSCard--level-3 mb-2">
            <CardHeader>
                <div
                    className="d-flex align-items-center justify-content-between"
                    role="button"
                    tabIndex={0}
                >
                    <div className="d-flex align-items-center">
                        <div>
                            <h5 className="mb-0 text-muted">{developmentName}</h5>
                            <h5 className="mb-1 mt-1 text-white">{projectName}</h5>
                        </div>
                    </div>
                    <div className="d-flex">
                        {
                            isExpandedState && (
                                <NSButton
                                    className="mr-2 heap-deal-documents-view-assc-project-library"
                                    color="dark"
                                    outline
                                    callback={RedirectToCoreDocumentPage}
                                    text="View"
                                />
                            )
                        }
                        <FontAwesomeIcon
                            data-testid="expandIcon"
                            className="icon my-auto cursor--pointer text-white heap-deal-documents-assc-projects-click"
                            onClick={toggleExpanded}
                            icon={isExpandedState ? faCaretUp : faCaretDown}
                            fixedWidth
                            size="lg"
                        />
                    </div>
                </div>
            </CardHeader>
            <Collapse isOpen={isExpandedState}>
                <div className="p-2">
                    <DropzoneCard getFilesFromEvent={getFilesFromEvent} accept="*" allowFolderUpload={false} />
                </div>
                {
                    projectAssets && projectAssets.length > 0 && (
                        <ProjectAssetTable projectAssets={projectAssets} subprojectId={subprojectId} />
                    )
                }
            </Collapse>
        </NSCard>
    );
};

export default ProjectCard;
